/**
 * Determines if an element is visible within the current viewport.
 *
 * This function checks if the given element is within the visible area of the browser window.
 * It considers an element visible if any part of it is within the viewport.
 *
 * @param {Element} element - The DOM element to check for visibility.
 * @returns {boolean} True if the element is visible, false otherwise.
 *
 * @example
 * // Using with React ref
 * const MyComponent = () => {
 *   const elementRef = useRef(null);
 *
 *   useEffect(() => {
 *     if (elementRef.current && isVisible(elementRef.current)) {
 *       console.log('The element is visible on mount!');
 *     }
 *   }, []);
 *
 *   return <div ref={elementRef}>Check my visibility</div>;
 * };
 *
 * @example
 * // Checking visibility on scroll
 * window.addEventListener('scroll', () => {
 *   const element = document.querySelector('.my-element');
 *   if (isVisible(element)) {
 *     console.log('Element became visible after scrolling!');
 *   }
 * });
 */
export function isVisible(element: Element): boolean {
    const boundingRect = element.getBoundingClientRect();

    // Check if the element's top and left values are within the viewport
    const { top } = boundingRect;
    const { left } = boundingRect;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    if (top > viewportHeight || left > viewportWidth) {
        return false;
    }

    // Check if the element's right and bottom values are within the viewport
    const { right } = boundingRect;
    const { bottom } = boundingRect;
    if (right < 0 || bottom < 0) {
        return false;
    }

    return true;
}
